import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
} from '@chakra-ui/react';
import { MdRefresh, MdUndo } from 'react-icons/md';
import { FiVolume2, FiVolumeX } from 'react-icons/fi';
import { ChevronDownIcon } from '@chakra-ui/icons';

const DIFF_LABELS = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

const controlH = '40px';
/** Outer bar height for mode / difficulty panel (controls stay `controlH`) */
const leftPanelBarH = '60px';
/** Outer bar height for sound / undo / new game panel (controls stay `controlH`) */
const rightPanelBarH = '60px';
/** Outer bar height for display panel (matches left/right rails) */
const displayPanelBarH = '60px';
const btnTransition =
  'background 0.14s ease, border-color 0.14s ease, color 0.14s ease, box-shadow 0.14s ease, transform 0.1s ease';

const controlPanelStyles = {
  bg: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  borderWidth: '1px',
  borderColor: 'gray.200',
  borderRadius: '2xl',
  px: { base: 2.5, md: 3 },
  py: { base: 2, md: 2.5 },
  boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 1px 3px rgba(15, 23, 42, 0.06)',
};

/**
 * @param {object} props
 * @param {'pvp' | 'ai'} props.mode
 * @param {(m: 'pvp' | 'ai') => void} props.onModeChange
 * @param {'easy' | 'medium' | 'hard'} props.difficulty
 * @param {(d: 'easy' | 'medium' | 'hard') => void} props.onDifficultyChange
 * @param {boolean} props.soundEnabled
 * @param {(v: boolean) => void} props.onSoundToggle
 * @param {() => void} props.onReset
 * @param {() => void} props.onUndo
 * @param {boolean} props.canUndo
 * @param {boolean} props.difficultyDisabled
 * @param {import('react').ReactNode} [props.displayPanel] in-play status / middle rail content
 */
function TicTacToeControls({
  mode,
  onModeChange,
  difficulty,
  onDifficultyChange,
  soundEnabled,
  onSoundToggle,
  onReset,
  onUndo,
  canUndo,
  difficultyDisabled,
  displayPanel,
}) {
  return (
    <Flex
      role="toolbar"
      aria-label="Game controls"
      w="full"
      justify="flex-start"
      align="center"
      gap={{ base: 3, md: 3 }}
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
    >
      {/* Left panel: mode + AI difficulty — 60px bar; inner controls remain 40px */}
      <Box
        {...controlPanelStyles}
        flex={{ base: '1 1 100%', md: '0 0 auto' }}
        minW={0}
        h={leftPanelBarH}
        py={0}
        display="flex"
        alignItems="center"
        overflow="hidden"
      >
        <HStack
          spacing={2}
          flexWrap="nowrap"
          align="center"
          justify="flex-start"
          w="full"
          minW={0}
        >
          <ButtonGroup
            isAttached
            size="md"
            variant="outline"
            borderColor="gray.200"
            borderRadius="xl"
            overflow="hidden"
            flexShrink={0}
            boxShadow="0 1px 2px rgba(15, 23, 42, 0.04)"
          >
            <Tooltip label="vs AI" hasArrow openDelay={280}>
              <IconButton
                aria-label="vs AI"
                aria-pressed={mode === 'ai'}
                icon={
                  <Box
                    as="span"
                    fontSize={mode === 'ai' ? '30px' : '24px'}
                    lineHeight={1}
                    role="img"
                    aria-hidden
                    transition="font-size 0.16s ease"
                    sx={{
                      filter:
                        mode === 'ai'
                          ? 'drop-shadow(0 0 2px rgba(20, 184, 166, 0.9)) drop-shadow(0 0 6px rgba(45, 212, 191, 0.55))'
                          : 'none',
                    }}
                  >
                    👤
                  </Box>
                }
                h={controlH}
                w={controlH}
                minW={controlH}
                borderRadius="xl"
                borderWidth="1px"
                variant="outline"
                borderColor={mode === 'ai' ? 'teal.200' : 'gray.200'}
                bg={mode === 'ai' ? 'teal.50' : 'white'}
                color={mode === 'ai' ? 'teal.600' : 'gray.600'}
                _hover={{
                  bg: mode === 'ai' ? 'teal.100' : 'gray.50',
                  borderColor: mode === 'ai' ? 'teal.300' : 'gray.300',
                  color: mode === 'ai' ? 'teal.700' : 'gray.700',
                }}
                _active={{ transform: 'scale(0.985)' }}
                transition={btnTransition}
                onClick={() => onModeChange('ai')}
              />
            </Tooltip>
            <Tooltip label="Two players" hasArrow openDelay={280}>
              <IconButton
                aria-label="Two players"
                aria-pressed={mode === 'pvp'}
                icon={
                  <Box
                    as="span"
                    fontSize={mode === 'pvp' ? '30px' : '24px'}
                    lineHeight={1}
                    role="img"
                    aria-hidden
                    transition="font-size 0.16s ease"
                    sx={{
                      filter:
                        mode === 'pvp'
                          ? 'drop-shadow(0 0 2px rgba(20, 184, 166, 0.9)) drop-shadow(0 0 6px rgba(45, 212, 191, 0.55))'
                          : 'none',
                    }}
                  >
                    👥
                  </Box>
                }
                h={controlH}
                w={controlH}
                minW={controlH}
                borderRadius="xl"
                borderWidth="1px"
                variant="outline"
                borderColor={mode === 'pvp' ? 'teal.200' : 'gray.200'}
                bg={mode === 'pvp' ? 'teal.50' : 'white'}
                color={mode === 'pvp' ? 'teal.600' : 'gray.600'}
                _hover={{
                  bg: mode === 'pvp' ? 'teal.100' : 'gray.50',
                  borderColor: mode === 'pvp' ? 'teal.300' : 'gray.300',
                  color: mode === 'pvp' ? 'teal.700' : 'gray.700',
                }}
                _active={{ transform: 'scale(0.985)' }}
                transition={btnTransition}
                onClick={() => onModeChange('pvp')}
              />
            </Tooltip>
          </ButtonGroup>

          <Menu placement="bottom-start" isLazy>
            <MenuButton
              as={Button}
              h={controlH}
              minW={{ base: '0', md: '140px' }}
              flex={{ base: 1, md: 'initial' }}
              rightIcon={<ChevronDownIcon />}
              variant="outline"
              borderRadius="xl"
              borderColor="gray.200"
              bg="white"
              fontWeight="600"
              fontSize="sm"
              boxShadow="0 1px 2px rgba(15, 23, 42, 0.04)"
              isDisabled={difficultyDisabled}
              opacity={difficultyDisabled ? 0.48 : 1}
              _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
              _active={{ transform: 'scale(0.985)' }}
              transition={btnTransition}
            >
              {difficultyDisabled ? 'AI —' : `AI · ${DIFF_LABELS[difficulty]}`}
            </MenuButton>
            <MenuList borderRadius="xl" py={1} minW="168px" shadow="lg" borderColor="gray.100">
              {['easy', 'medium', 'hard'].map((d) => (
                <MenuItem
                  key={d}
                  fontWeight={difficulty === d ? '700' : '500'}
                  bg={difficulty === d ? 'teal.50' : undefined}
                  borderRadius="md"
                  mx={1}
                  onClick={() => onDifficultyChange(d)}
                >
                  {DIFF_LABELS[d]}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
      </Box>

      {/* Display panel — in-play status; grows on desktop between left and right rails */}
      <Box
        {...controlPanelStyles}
        aria-label="Game status display"
        flex={{ base: '1 1 100%', md: '1 1 auto' }}
        minW={0}
        h={displayPanelBarH}
        py={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        {displayPanel}
      </Box>

      {/* Right panel: sound, undo, new game — fixed bar; inner controls remain 40px */}
      <Box
        {...controlPanelStyles}
        flex={{ base: '1 1 100%', md: '0 0 auto' }}
        minW={0}
        h={rightPanelBarH}
        py={0}
        display="flex"
        alignItems="center"
        overflow="hidden"
      >
        <HStack
          spacing={2}
          flexWrap="nowrap"
          justify={{ base: 'space-between', md: 'flex-end' }}
          w="full"
          align="center"
          minW={0}
        >
          <Tooltip label="Sound effects" hasArrow openDelay={280}>
            <IconButton
              aria-label={soundEnabled ? 'Turn sound off' : 'Turn sound on'}
              icon={soundEnabled ? <FiVolume2 size={18} /> : <FiVolumeX size={18} />}
              h={controlH}
              w={controlH}
              minW={controlH}
              variant="outline"
              borderRadius="xl"
              borderColor="gray.200"
              bg="white"
              color="gray.600"
              boxShadow="0 1px 2px rgba(15, 23, 42, 0.04)"
              _hover={{ bg: 'gray.50', borderColor: 'gray.300', color: 'gray.800' }}
              _active={{ transform: 'scale(0.94)' }}
              transition={btnTransition}
              onClick={() => onSoundToggle(!soundEnabled)}
            />
          </Tooltip>

          <Tooltip label="Undo last move" hasArrow openDelay={280}>
            <IconButton
              aria-label="Undo last move"
              icon={<MdUndo size={18} />}
              h={controlH}
              w={controlH}
              minW={controlH}
              variant="outline"
              borderRadius="xl"
              borderColor="gray.200"
              bg="white"
              color="gray.600"
              boxShadow="0 1px 2px rgba(15, 23, 42, 0.04)"
              isDisabled={!canUndo}
              _hover={{ bg: 'gray.50', borderColor: 'gray.300', color: 'gray.800' }}
              _active={{ transform: 'scale(0.94)' }}
              transition={btnTransition}
              onClick={onUndo}
            />
          </Tooltip>

          <Divider
            orientation="vertical"
            borderColor="gray.200"
            h={controlH}
            display={{ base: 'none', sm: 'block' }}
            alignSelf="center"
            opacity={0.85}
          />

          <Button
            h={controlH}
            px={5}
            leftIcon={<MdRefresh size={18} />}
            colorScheme="teal"
            variant="solid"
            borderRadius="xl"
            fontWeight="700"
            fontSize="sm"
            flex={{ base: 1, sm: '0 0 auto' }}
            bg="teal.500"
            boxShadow="0 1px 2px rgba(13, 148, 136, 0.25), 0 0 0 1px rgba(13, 148, 136, 0.15) inset"
            _hover={{
              bg: 'teal.600',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 14px -2px rgba(13, 148, 136, 0.45)',
            }}
            _active={{ transform: 'translateY(0)', bg: 'teal.700' }}
            transition={btnTransition}
            onClick={onReset}
          >
            New game
          </Button>
        </HStack>
      </Box>
    </Flex>
  );
}

export default TicTacToeControls;
